import { useState } from 'react';
import { Virtuoso } from 'react-virtuoso';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Card,
  Typography,
  Stack,
  Divider,
  Link as MuiLink,
  Chip,
} from '@mui/material';
import Loader from '../../../components/Loader';
import { FilterPopover } from '../../internalSearch/components/FilterPopover';
import { mapAgreementStatus } from '../../internalSearch/util/agreement-status';
import {
  MasterListFilter,
  MasterListFilterKeys,
  MasterListJournal,
  MaterialListStatus,
} from '../../../types/MasterList.types';
import styled from '@emotion/styled';
import { statusColors } from '../utils/StatusColor';
import { getStatusLabel, isStatusVerified } from '../utils/status';
import dayjs from 'dayjs';

const StyledStatusChip = styled(Chip)<{
  status: MaterialListStatus | undefined;
}>`
  && {
    background-color: ${(props) =>
      props.status ? statusColors[props.status] : 'transparent'};
    text-transform: capitalize;
  }
`;

interface JournalsTableMobileProps {
  journals: MasterListJournal[];
  filters: MasterListFilter;
  selectedFilters: MasterListFilter;
  hasFiltersApplied?: boolean;
  isLoading?: boolean;
  onToggleFilter: (type: MasterListFilterKeys, value: string) => void;
  onJournalClick: (journal: MasterListJournal) => void;
}

export const JournalsTableMobile = ({
  journals,
  filters,
  selectedFilters,
  hasFiltersApplied = false,
  isLoading = false,
  onToggleFilter,
  onJournalClick,
}: JournalsTableMobileProps) => {
  const { t } = useTranslation();
  const [openFilter, setOpenFilter] = useState<{
    key: MasterListFilterKeys;
    anchor: HTMLElement;
  } | null>(null);

  const hasJournals = journals && journals.length > 0;

  return (
    <Stack spacing={2} sx={{ mt: 3 }}>
      {(isLoading || hasJournals || hasFiltersApplied) && (
        <Card
          variant="default"
          aria-label={t('masterList.filtersAriaLabel') as string}
          sx={{
            p: 24,
            my: 16,
            backgroundColor: 'var(--color-scifree-teal)',
          }}
        >
          <Stack direction="row" sx={{ flexWrap: 'wrap', gap: 16 }}>
            <FilterPopover
              label={t('masterList.columnPublisher')}
              options={filters.publishers}
              selectedOptions={selectedFilters.publishers}
              onToggle={(publisher) => onToggleFilter('publishers', publisher)}
              anchorEl={
                openFilter?.key === 'publishers' ? openFilter.anchor : null
              }
              open={openFilter?.key === 'publishers'}
              onOpen={(e) =>
                setOpenFilter({ key: 'publishers', anchor: e.currentTarget })
              }
              onClose={() => setOpenFilter(null)}
            />
            <FilterPopover
              label={t('masterList.columnImprint')}
              options={filters.imprints}
              selectedOptions={selectedFilters.imprints}
              onToggle={(imprint) => onToggleFilter('imprints', imprint)}
              anchorEl={
                openFilter?.key === 'imprints' ? openFilter.anchor : null
              }
              open={openFilter?.key === 'imprints'}
              onOpen={(e) =>
                setOpenFilter({ key: 'imprints', anchor: e.currentTarget })
              }
              onClose={() => setOpenFilter(null)}
            />
            <FilterPopover
              label={t('masterList.columnLicenseOption')}
              options={filters.licenses}
              selectedOptions={selectedFilters.licenses}
              onToggle={(license) => onToggleFilter('licenses', license)}
              anchorEl={
                openFilter?.key === 'licenses' ? openFilter.anchor : null
              }
              open={openFilter?.key === 'licenses'}
              onOpen={(e) =>
                setOpenFilter({ key: 'licenses', anchor: e.currentTarget })
              }
              onClose={() => setOpenFilter(null)}
            />

            <FilterPopover
              label={t('masterList.columnPublishingModel')}
              options={filters.publishingModels}
              selectedOptions={selectedFilters.publishingModels}
              onToggle={(model) => onToggleFilter('publishingModels', model)}
              anchorEl={
                openFilter?.key === 'publishingModels'
                  ? openFilter.anchor
                  : null
              }
              open={openFilter?.key === 'publishingModels'}
              onOpen={(e) =>
                setOpenFilter({
                  key: 'publishingModels',
                  anchor: e.currentTarget,
                })
              }
              onClose={() => setOpenFilter(null)}
            />
            <FilterPopover
              label={t('masterList.columnJournalStatus')}
              options={filters.statuses}
              selectedOptions={selectedFilters.statuses}
              onToggle={(status) => onToggleFilter('statuses', status)}
              anchorEl={
                openFilter?.key === 'statuses' ? openFilter.anchor : null
              }
              open={openFilter?.key === 'statuses'}
              onOpen={(e) =>
                setOpenFilter({ key: 'statuses', anchor: e.currentTarget })
              }
              onClose={() => setOpenFilter(null)}
              optionLabelMapper={mapAgreementStatus}
            />
          </Stack>
        </Card>
      )}
      <Box sx={{ mt: 2, height: '640px' }}>
        {isLoading ? (
          <Loader />
        ) : (
          <Virtuoso
            style={{ height: '100%' }}
            data={journals}
            itemContent={(index, journal) => {
              const journalLabel =
                journal.name || journal.issn_e || `Journal ${index + 1}`;
              return (
                <Box sx={{ mb: 12 }}>
                  <Card
                    variant="primary"
                    tabIndex={0}
                    aria-label={
                      t('agreements.openJournal', {
                        name: journalLabel,
                        defaultValue: `Open journal, ${journalLabel}`,
                      }) as string
                    }
                    onClick={() => onJournalClick(journal)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        onJournalClick(journal);
                      }
                    }}
                    sx={{
                      cursor: 'pointer',
                      ...(!isStatusVerified(journal.status)
                        ? {
                            backgroundColor: 'var(--color-very-pale-orange)',
                            '&:hover': {
                              backgroundColor: 'var(--color-very-pale-orange)',
                            },
                          }
                        : {}),
                      '&:focus-visible': {
                        outline: '2px solid var(--color-scifree-teal)',
                        outlineOffset: '-2px',
                      },
                    }}
                  >
                    <Stack spacing={2}>
                      <Box>
                        <Typography variant="caption">
                          {t('masterList.columnTitle')}
                        </Typography>
                        <Typography variant="body1">
                          <MuiLink
                            href={journal.url}
                            target="_blank"
                            rel="noreferrer"
                            onClick={(e) => e.stopPropagation()}
                          >
                            {journal.name}
                          </MuiLink>
                        </Typography>
                      </Box>
                      <Divider />
                      <Box>
                        <Typography variant="caption">
                          {t('masterList.columnPublisher')}
                        </Typography>
                        <Typography variant="body1">
                          {journal.publisher || '-'}
                        </Typography>
                      </Box>
                      <Divider />
                      <Box>
                        <Typography variant="caption">
                          {t('masterList.columnImprint')}
                        </Typography>
                        <Typography variant="body1">
                          {journal.imprint || '-'}
                        </Typography>
                      </Box>
                      <Divider />
                      <Box>
                        <Typography variant="caption">
                          {t('masterList.columnLicenseOption')}
                        </Typography>
                        <Typography variant="body1">
                          {journal.licenses?.join(', ') || '-'}
                        </Typography>
                      </Box>
                      <Divider />
                      <Box>
                        <Typography variant="caption">
                          {t('masterList.columnPublishingModel')}
                        </Typography>
                        <Typography variant="body1">
                          {journal.publishing_model || '-'}
                        </Typography>
                      </Box>
                      <Divider />
                      <Box>
                        <Typography variant="caption">
                          {t('masterList.columnIssnE')}
                        </Typography>
                        <Typography variant="body1">
                          {journal.issn_e || '-'}
                        </Typography>
                      </Box>
                      <Divider />
                      <Box>
                        <Typography variant="caption">
                          {t('masterList.columnIssnP')}
                        </Typography>
                        <Typography variant="body1">
                          {journal.issn_p || '-'}
                        </Typography>
                      </Box>
                      <Divider />
                      <Box>
                        <Typography variant="caption">
                          {t('masterList.columnSubject')}
                        </Typography>
                        <Typography variant="body1">
                          {journal.subjects || '-'}
                        </Typography>
                      </Box>
                      <Divider />
                      <Box>
                        <Typography variant="caption">
                          {t('masterList.columnUpdatedAt')}
                        </Typography>
                        <Typography variant="body1">
                          {dayjs.utc(journal.updated_at).local().format('YYYY-MM-DD')}
                        </Typography>
                      </Box>
                      <Divider />
                      <Box>
                        <Typography variant="caption">
                          {t('masterList.columnUpdatedBy')}
                        </Typography>
                        <Typography variant="body1">
                          {journal.updated_by?.trim() || '-'}
                        </Typography>
                      </Box>
                      <Divider />
                      <Box>
                        <Typography variant="caption">
                          {t('masterList.columnJournalStatus')}
                        </Typography>
                        <Typography variant="body1" component="div">
                          {journal.status != null ? (
                            <StyledStatusChip
                              status={journal.status}
                              size="small"
                              label={getStatusLabel(journal.status)}
                            />
                          ) : (
                            '-'
                          )}
                        </Typography>
                      </Box>
                    </Stack>
                  </Card>
                </Box>
              );
            }}
          />
        )}
      </Box>
    </Stack>
  );
};
